import React, { useState, useEffect, useCallback } from 'react';
import {
    Page,
    Layout,
    Card,
    BlockStack,
    InlineStack,
    Text,
    Button,
    Spinner,
    Banner,
    Badge,
    DataTable,
    Modal,
    FormLayout,
    TextField,
    Select,
    Checkbox,
    Tabs,
    EmptyState
} from '@shopify/polaris';
import { bankAccountService } from '../services/bankAccountService';
import { paymentProviderService } from '../services/paymentProviderService';

const AccountingSettingsPage = () => {
    // Tab state
    const [selectedTab, setSelectedTab] = useState(0);

    // Bank accounts state
    const [bankAccounts, setBankAccounts] = useState([]);
    const [bankAccountsLoading, setBankAccountsLoading] = useState(true);

    // Payment providers state
    const [providers, setProviders] = useState([]);
    const [providersLoading, setProvidersLoading] = useState(true);
    const [bankAccountOptions, setBankAccountOptions] = useState([]);

    // Common state
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // Bank account modal state
    const [showBankModal, setShowBankModal] = useState(false);
    const [editingBankAccount, setEditingBankAccount] = useState(null);
    const [bankForm, setBankForm] = useState({
        account_name: '',
        bank_name: '',
        account_number: '',
        iban: '',
        swift_code: '',
        branch_name: '',
        is_default: false
    });
    const [bankSubmitting, setBankSubmitting] = useState(false);

    // Provider modal state
    const [showProviderModal, setShowProviderModal] = useState(false);
    const [editingProvider, setEditingProvider] = useState(null);
    const [providerForm, setProviderForm] = useState({
        name: '',
        code: '',
        type: 'cod_collection',
        default_bank_account_id: '',
        fee_type: 'percentage',
        fee_value: '',
        settlement_frequency: 'weekly'
    });
    const [providerSubmitting, setProviderSubmitting] = useState(false);

    const tabs = [
        { id: 'bank-accounts', content: 'Bank Accounts', panelID: 'bank-accounts-panel' },
        { id: 'payment-providers', content: 'Payment Providers', panelID: 'payment-providers-panel' }
    ];

    // Load bank accounts
    const loadBankAccounts = useCallback(async () => {
        setBankAccountsLoading(true);
        try {
            const response = await bankAccountService.getAll();
            setBankAccounts(response.data || []);

            // Also get select options for provider form
            const optionsRes = await bankAccountService.getSelectOptions();
            setBankAccountOptions(optionsRes.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setBankAccountsLoading(false);
        }
    }, []);

    // Load payment providers
    const loadProviders = useCallback(async () => {
        setProvidersLoading(true);
        try {
            const response = await paymentProviderService.getAll();
            setProviders(response.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setProvidersLoading(false);
        }
    }, []);

    useEffect(() => {
        loadBankAccounts();
        loadProviders();
    }, [loadBankAccounts, loadProviders]);

    // Bank account handlers
    const handleOpenBankModal = (account = null) => {
        if (account) {
            setEditingBankAccount(account);
            setBankForm({
                account_name: account.account_name || '',
                bank_name: account.bank_name || '',
                account_number: account.account_number || '',
                iban: account.iban || '',
                swift_code: account.swift_code || '',
                branch_name: account.branch_name || '',
                is_default: account.is_default || false
            });
        } else {
            setEditingBankAccount(null);
            setBankForm({
                account_name: '',
                bank_name: '',
                account_number: '',
                iban: '',
                swift_code: '',
                branch_name: '',
                is_default: false
            });
        }
        setShowBankModal(true);
    };

    const handleSaveBankAccount = async () => {
        setBankSubmitting(true);
        try {
            if (editingBankAccount) {
                await bankAccountService.update(editingBankAccount.account_id, bankForm);
                setSuccessMessage('Bank account updated successfully');
            } else {
                await bankAccountService.create(bankForm);
                setSuccessMessage('Bank account created successfully');
            }
            setShowBankModal(false);
            loadBankAccounts();
        } catch (err) {
            setError(err.message);
        } finally {
            setBankSubmitting(false);
        }
    };

    const handleDeleteBankAccount = async (accountId) => {
        if (!window.confirm('Are you sure you want to deactivate this bank account?')) return;
        try {
            await bankAccountService.delete(accountId);
            setSuccessMessage('Bank account deactivated');
            loadBankAccounts();
        } catch (err) {
            setError(err.message);
        }
    };

    // Provider handlers
    const handleOpenProviderModal = (provider = null) => {
        if (provider) {
            setEditingProvider(provider);
            setProviderForm({
                name: provider.name || '',
                code: provider.code || '',
                type: provider.type || 'cod_collection',
                default_bank_account_id: provider.default_bank_account_id || '',
                fee_type: provider.fee_type || 'percentage',
                fee_value: provider.fee_value?.toString() || '',
                settlement_frequency: provider.settlement_frequency || 'weekly'
            });
        } else {
            setEditingProvider(null);
            setProviderForm({
                name: '',
                code: '',
                type: 'cod_collection',
                default_bank_account_id: '',
                fee_type: 'percentage',
                fee_value: '',
                settlement_frequency: 'weekly'
            });
        }
        setShowProviderModal(true);
    };

    const handleSaveProvider = async () => {
        setProviderSubmitting(true);
        try {
            const data = {
                ...providerForm,
                fee_value: parseFloat(providerForm.fee_value) || 0
            };

            if (editingProvider) {
                await paymentProviderService.update(editingProvider.provider_id, data);
                setSuccessMessage('Payment provider updated successfully');
            } else {
                await paymentProviderService.create(data);
                setSuccessMessage('Payment provider created successfully');
            }
            setShowProviderModal(false);
            loadProviders();
        } catch (err) {
            setError(err.message);
        } finally {
            setProviderSubmitting(false);
        }
    };

    const handleDeleteProvider = async (providerId) => {
        if (!window.confirm('Are you sure you want to deactivate this payment provider?')) return;
        try {
            await paymentProviderService.delete(providerId);
            setSuccessMessage('Payment provider deactivated');
            loadProviders();
        } catch (err) {
            setError(err.message);
        }
    };

    // Render bank accounts table
    const renderBankAccountsTable = () => {
        if (bankAccountsLoading) {
            return (
                <InlineStack align="center">
                    <Spinner size="large" />
                </InlineStack>
            );
        }

        if (bankAccounts.length === 0) {
            return (
                <EmptyState
                    heading="No bank accounts"
                    action={{ content: 'Add Bank Account', onAction: () => handleOpenBankModal() }}
                >
                    <p>Add your company bank accounts to track settlements.</p>
                </EmptyState>
            );
        }

        const rows = bankAccounts.map(account => [
            <BlockStack gap="100">
                <Text as="span" fontWeight="semibold">{account.account_name}</Text>
                {account.is_default && <Badge tone="info">Default</Badge>}
            </BlockStack>,
            account.bank_name || '-',
            account.account_number ? `****${account.account_number.slice(-4)}` : '-',
            <Badge tone={account.status === 'active' ? 'success' : 'warning'}>
                {account.status}
            </Badge>,
            <InlineStack gap="200">
                <Button size="slim" onClick={() => handleOpenBankModal(account)}>Edit</Button>
                <Button size="slim" tone="critical" onClick={() => handleDeleteBankAccount(account.account_id)}>
                    Delete
                </Button>
            </InlineStack>
        ]);

        return (
            <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                headings={['Account Name', 'Bank', 'Account #', 'Status', 'Actions']}
                rows={rows}
            />
        );
    };

    // Render providers table
    const renderProvidersTable = () => {
        if (providersLoading) {
            return (
                <InlineStack align="center">
                    <Spinner size="large" />
                </InlineStack>
            );
        }

        if (providers.length === 0) {
            return (
                <EmptyState
                    heading="No payment providers"
                    action={{ content: 'Add Provider', onAction: () => handleOpenProviderModal() }}
                >
                    <p>Add payment providers like Bosta, Fawry, etc.</p>
                </EmptyState>
            );
        }

        const rows = providers.map(provider => [
            <BlockStack gap="100">
                <Text as="span" fontWeight="semibold">{provider.name}</Text>
                <Text as="span" tone="subdued" variant="bodySm">{provider.code}</Text>
            </BlockStack>,
            provider.type?.replace('_', ' ') || '-',
            provider.fee_type === 'percentage'
                ? `${provider.fee_value}%`
                : `${provider.fee_value} EGP`,
            provider.default_bank_account_name || <Text tone="subdued">Not linked</Text>,
            <Badge tone={provider.status === 'active' ? 'success' : 'warning'}>
                {provider.status}
            </Badge>,
            <InlineStack gap="200">
                <Button size="slim" onClick={() => handleOpenProviderModal(provider)}>Edit</Button>
                <Button size="slim" tone="critical" onClick={() => handleDeleteProvider(provider.provider_id)}>
                    Delete
                </Button>
            </InlineStack>
        ]);

        return (
            <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                headings={['Provider', 'Type', 'Fee', 'Bank Account', 'Status', 'Actions']}
                rows={rows}
            />
        );
    };

    return (
        <Page
            title="Accounting Settings"
            subtitle="Manage bank accounts and payment providers"
        >
            <Layout>
                {error && (
                    <Layout.Section>
                        <Banner tone="critical" onDismiss={() => setError(null)}>
                            {error}
                        </Banner>
                    </Layout.Section>
                )}

                {successMessage && (
                    <Layout.Section>
                        <Banner tone="success" onDismiss={() => setSuccessMessage(null)}>
                            {successMessage}
                        </Banner>
                    </Layout.Section>
                )}

                <Layout.Section>
                    <Card>
                        <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
                            <BlockStack gap="400">
                                {selectedTab === 0 && (
                                    <>
                                        <InlineStack align="space-between">
                                            <Text as="h2" variant="headingMd">Bank Accounts</Text>
                                            <Button variant="primary" onClick={() => handleOpenBankModal()}>
                                                Add Bank Account
                                            </Button>
                                        </InlineStack>
                                        {renderBankAccountsTable()}
                                    </>
                                )}

                                {selectedTab === 1 && (
                                    <>
                                        <InlineStack align="space-between">
                                            <Text as="h2" variant="headingMd">Payment Providers</Text>
                                            <Button variant="primary" onClick={() => handleOpenProviderModal()}>
                                                Add Provider
                                            </Button>
                                        </InlineStack>
                                        {renderProvidersTable()}
                                    </>
                                )}
                            </BlockStack>
                        </Tabs>
                    </Card>
                </Layout.Section>
            </Layout>

            {/* Bank Account Modal */}
            <Modal
                open={showBankModal}
                onClose={() => setShowBankModal(false)}
                title={editingBankAccount ? 'Edit Bank Account' : 'Add Bank Account'}
                primaryAction={{
                    content: 'Save',
                    onAction: handleSaveBankAccount,
                    loading: bankSubmitting,
                    disabled: !bankForm.account_name || !bankForm.bank_name
                }}
                secondaryActions={[{
                    content: 'Cancel',
                    onAction: () => setShowBankModal(false)
                }]}
            >
                <Modal.Section>
                    <FormLayout>
                        <TextField
                            label="Account Name"
                            value={bankForm.account_name}
                            onChange={(value) => setBankForm({ ...bankForm, account_name: value })}
                            placeholder="e.g., CIB Main Account"
                            autoComplete="off"
                        />
                        <TextField
                            label="Bank Name"
                            value={bankForm.bank_name}
                            onChange={(value) => setBankForm({ ...bankForm, bank_name: value })}
                            placeholder="e.g., Commercial International Bank"
                            autoComplete="off"
                        />
                        <TextField
                            label="Account Number"
                            value={bankForm.account_number}
                            onChange={(value) => setBankForm({ ...bankForm, account_number: value })}
                            autoComplete="off"
                        />
                        <TextField
                            label="IBAN"
                            value={bankForm.iban}
                            onChange={(value) => setBankForm({ ...bankForm, iban: value })}
                            autoComplete="off"
                        />
                        <TextField
                            label="SWIFT Code"
                            value={bankForm.swift_code}
                            onChange={(value) => setBankForm({ ...bankForm, swift_code: value })}
                            autoComplete="off"
                        />
                        <TextField
                            label="Branch Name"
                            value={bankForm.branch_name}
                            onChange={(value) => setBankForm({ ...bankForm, branch_name: value })}
                            autoComplete="off"
                        />
                        <Checkbox
                            label="Set as default account"
                            checked={bankForm.is_default}
                            onChange={(checked) => setBankForm({ ...bankForm, is_default: checked })}
                        />
                    </FormLayout>
                </Modal.Section>
            </Modal>

            {/* Provider Modal */}
            <Modal
                open={showProviderModal}
                onClose={() => setShowProviderModal(false)}
                title={editingProvider ? 'Edit Payment Provider' : 'Add Payment Provider'}
                primaryAction={{
                    content: 'Save',
                    onAction: handleSaveProvider,
                    loading: providerSubmitting,
                    disabled: !providerForm.name || !providerForm.code
                }}
                secondaryActions={[{
                    content: 'Cancel',
                    onAction: () => setShowProviderModal(false)
                }]}
            >
                <Modal.Section>
                    <FormLayout>
                        <TextField
                            label="Provider Name"
                            value={providerForm.name}
                            onChange={(value) => setProviderForm({ ...providerForm, name: value })}
                            placeholder="e.g., Bosta"
                            autoComplete="off"
                        />
                        <TextField
                            label="Provider Code"
                            value={providerForm.code}
                            onChange={(value) => setProviderForm({ ...providerForm, code: value.toLowerCase().replace(/\s/g, '_') })}
                            placeholder="e.g., bosta"
                            helpText="Unique identifier (lowercase, no spaces)"
                            autoComplete="off"
                        />
                        <Select
                            label="Type"
                            options={[
                                { label: 'COD Collection', value: 'cod_collection' },
                                { label: 'Payment Gateway', value: 'payment_gateway' },
                                { label: 'Mobile Wallet', value: 'mobile_wallet' },
                                { label: 'Bank Transfer', value: 'bank_transfer' }
                            ]}
                            value={providerForm.type}
                            onChange={(value) => setProviderForm({ ...providerForm, type: value })}
                        />
                        <Select
                            label="Default Bank Account"
                            options={[
                                { label: 'Select bank account...', value: '' },
                                ...bankAccountOptions.map(acc => ({
                                    label: acc.label,
                                    value: acc.value
                                }))
                            ]}
                            value={providerForm.default_bank_account_id}
                            onChange={(value) => setProviderForm({ ...providerForm, default_bank_account_id: value })}
                            helpText="Settlements from this provider will go to this account"
                        />
                        <InlineStack gap="400">
                            <Select
                                label="Fee Type"
                                options={[
                                    { label: 'Percentage', value: 'percentage' },
                                    { label: 'Fixed Amount', value: 'fixed' }
                                ]}
                                value={providerForm.fee_type}
                                onChange={(value) => setProviderForm({ ...providerForm, fee_type: value })}
                            />
                            <TextField
                                label="Fee Value"
                                type="number"
                                value={providerForm.fee_value}
                                onChange={(value) => setProviderForm({ ...providerForm, fee_value: value })}
                                suffix={providerForm.fee_type === 'percentage' ? '%' : 'EGP'}
                                autoComplete="off"
                            />
                        </InlineStack>
                        <Select
                            label="Settlement Frequency"
                            options={[
                                { label: 'Daily', value: 'daily' },
                                { label: 'Weekly', value: 'weekly' },
                                { label: 'Bi-weekly', value: 'biweekly' },
                                { label: 'Monthly', value: 'monthly' }
                            ]}
                            value={providerForm.settlement_frequency}
                            onChange={(value) => setProviderForm({ ...providerForm, settlement_frequency: value })}
                        />
                    </FormLayout>
                </Modal.Section>
            </Modal>
        </Page>
    );
};

export default AccountingSettingsPage;
